import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from "@angular/core";
import { combineLatest, Observable, of, Subject, take, takeUntil } from "rxjs";
import { createStandingAuthMessage, eAuthNotifications, transactionTypes } from "../../../shared/utilities/Constants";
import { Store } from "@ngrx/store";
import { selectEauthMissingEmail } from "../../../setup-eAuthorization/reducers";
import { selectEAuthEmailAddressError, selectEAuthIsOptedOut } from "../../../shared/reducers";
import { selectEAuthAccountHolderWithFpoaOrFtaSelected, selectIsSubmitAndCreate, selectSaveAsTemplateToggledOn, selectStagedTransactionForm, selectStandingAuthEauthClientSelectedIsStandingAuthEligible, selectSubmitAndCreateEauthIneligible, selectTransactionType, selectUseStandingAuthVal, selectWireInfo } from "../../reducers";
import { selectIsOrgAccount, selectRegType } from "../../../account-info-header/reducers";
import { TransactionForm } from "../../models/form/TransactionForm";
import { TransactionFormActions } from "../../actions";
import { selectIsCreateSloaToggleFlagEnabled, selectIsEnableWiresCondensedLayout } from "../../../feature-flags/reducers";
import { StandingAuthInternalTransferActions, StandingAuthWireActions } from "../../../standing-authorizations/actions";
import { selectStandingAuthInternalTransferCustomRuleSplitCallHasError, selectStandingAuthWireCustomRuleSplitCallHasError, selectStandingAuthWireInfo } from "../../../standing-authorizations/reducers";
import { selectHasAccessInitiateMoneyMovement } from "../../../authorize/reducers";
import { TransactionType } from "../../../shared/types/Transaction";
import { CoreService, ICurrentContext } from "@charlesschwab/sac-ui-core";
import { ContextType } from "@charlesschwab/as-ui-core";
import { ValidateWireFlagsService } from "../../../shared/services/validate-WireFlags.service";
import { isPalAccountFlow } from "../../../shared/utilities/AccountFlow";

@Component({
  standalone: false,
  selector: 'mm-submit-and-create-option',
  templateUrl: './submit-and-create-option.component.html',
  styleUrls: ['./submit-and-create-option.component.scss'],
})
export class SubmitAndCreateOptionComponent implements OnInit, OnDestroy {
  @Input()
  showSubmitAndCreate: boolean | null = false;
  @Input()
  isClientAuthShown: boolean = true;

  @Input()
  shouldToggleOff$: Observable<boolean> = of(true);

  @Output()
  isSubmitAndCreate: EventEmitter<boolean> = new EventEmitter();
  missingEmailMessage = eAuthNotifications.missingEmailMessage;
  ineligibleEmailMessage = eAuthNotifications.submitAndCreateIneligibleMessage;
  destroy$: Subject<boolean> = new Subject<boolean>();
  isOptedOut: boolean = false;
  showMissingEmailMessage$: Observable<boolean>;
  submitAndCreateToggledOn: boolean;
  showEAuthEmailAddressError: boolean;
  blockSubmitAndCreate: boolean;
  isOrgAccount: boolean;
  isTrustAccount: boolean;
  createStandingAuthDetail: string = createStandingAuthMessage.allAccountHolders;
  createStandingAuthBundledDetail: string = createStandingAuthMessage.bundledAccount;
  createStandingAuthTitle: string = 'Create standing authorization';
  createBundledStandingAuthTitle: string = 'Create bundled standing authorization (optional)';
  toggleOff: boolean = true;
  isBundledOnetime: boolean;
  transactionForm: TransactionForm;
  hiddenToggle: boolean = false;
  isCreateSloaToggleFlagEnabled: boolean = false;
  hasMmInitiateAccess: boolean = false;
  useCondensed: boolean = false;
  transactionType: TransactionType | '';
  isPalAccount: boolean = false;

  constructor(
    private store: Store,
    private coreService: CoreService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { }

  ngOnInit(): void {
    this.showMissingEmailMessage$ = this.store.select(selectEauthMissingEmail);

    this.shouldToggleOff$?.pipe(takeUntil(this.destroy$)).subscribe((shouldToggleOff) => {
      if (shouldToggleOff) {
        this.toggleOff = true;
      }
    });

    this.store.select(selectEAuthIsOptedOut).pipe(takeUntil(this.destroy$)).subscribe(isOptedOut => {
      this.isOptedOut = isOptedOut;
    });

    this.store.select(selectStagedTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.transactionForm = data;
    });

    this.store
      .select(selectTransactionType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((transactionType) => {
        this.transactionType = transactionType;
      });

    this.store
      .select(selectIsEnableWiresCondensedLayout)
      .pipe(takeUntil(this.destroy$))
      .subscribe((flag) => {
        this.useCondensed =
          flag && this.transactionType === transactionTypes.wire;
      });

    combineLatest([
      this.store.select(selectSaveAsTemplateToggledOn),
      this.store.select(selectIsSubmitAndCreate)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([saveAsTemplateToggledOn, submitAndCreateToggledOn]) => {
      this.submitAndCreateToggledOn = submitAndCreateToggledOn && !saveAsTemplateToggledOn;
    })

    this.store.select(selectEAuthEmailAddressError).pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.showEAuthEmailAddressError = error
    });

    this.store.select(selectSubmitAndCreateEauthIneligible).pipe(takeUntil(this.destroy$)).subscribe(eauthIneligible => {
      this.blockSubmitAndCreate = eauthIneligible
    });

    this.store.select(selectIsCreateSloaToggleFlagEnabled).pipe(takeUntil(this.destroy$)).subscribe(flag => {
      this.isCreateSloaToggleFlagEnabled = flag
    });

    this.store.select(selectHasAccessInitiateMoneyMovement)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.hasMmInitiateAccess = response;
      });

    this.setIsPalAccount(this.coreService.currentContext);
    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.setIsPalAccount(context);
      });

    combineLatest([
      this.store.select(selectIsOrgAccount),
      this.store.select(selectRegType)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([isOrgAccount, regType]) => {
      this.isOrgAccount = isOrgAccount;
      this.isTrustAccount = regType?.includes("Liv Trust");
      this.createStandingAuthDetail = isOrgAccount || this.isTrustAccount ? createStandingAuthMessage.orgTrustAccounts : this.createStandingAuthDetail;
    })

    this.store.select(selectUseStandingAuthVal).pipe(takeUntil(this.destroy$)).subscribe((standingAuthVal) => {
      this.isBundledOnetime = standingAuthVal === 'bundled-no';
    });

    combineLatest([
      this.store.select(selectEAuthAccountHolderWithFpoaOrFtaSelected),
      this.store.select(selectStandingAuthEauthClientSelectedIsStandingAuthEligible)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([fpoaOrFtaSelected, eauthEligibleSelected]) => {
      if (fpoaOrFtaSelected || !eauthEligibleSelected) {
        this.onToggleOff();
        this.hiddenToggle = true;
      } else {
        this.hiddenToggle = false;
      }
    });
  }

  private setIsPalAccount(context?: ICurrentContext): void {
    this.isPalAccount = isPalAccountFlow(
      context?.contextType ?? ContextType.Firm,
      this.validateWireFlagsService.enablePalWireCreateTransaction()
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  /**
   *
   * @param toggleValue Boolean value from the toggle switch. true = checked/ON, false = unchecked/OFF
   *
   */
  onToggleChange(toggleValue: Event) {
    const e = toggleValue as CustomEvent<boolean>;
    if (e.detail) {
      this.toggleOff = false;
    }
    this.isSubmitAndCreate.emit(e.detail);
  }

  onChangeToggle(toggleValue: boolean) {
    this.isSubmitAndCreate.emit(toggleValue);
    if (toggleValue) {
      this.toggleOff = false;
      if (this.transactionForm.transactionType?.toLowerCase() == 'wire') {
        if (this.isCreateSloaToggleFlagEnabled && this.hasMmInitiateAccess) {
          this.store.select(selectStandingAuthWireInfo).pipe(take(1)).subscribe(state => {
            this.store.dispatch(StandingAuthWireActions.clearStandingAuthWireAuthResponse({ state }));
          });
          this.store.dispatch(TransactionFormActions.callWireSloaVerifyV3FromCreateTransactionCustomRulesSplit(this.transactionForm));
          this.store.select(selectStandingAuthWireCustomRuleSplitCallHasError).pipe(takeUntil(this.destroy$)).subscribe(hasError => {
            if (hasError) {
              this.toggleOff = hasError;
            }
          });
        }
        else {
          this.store.dispatch(TransactionFormActions.callWireSloaVerifyV3FromCreateTransaction(this.transactionForm));
          this.store.select(selectWireInfo).pipe(takeUntil(this.destroy$)).subscribe(wireInfo => {
            this.toggleOff = wireInfo.OneTimeAndSloaWiresVerifyV3Failed;
          });
        }
      }
      else if (this.transactionForm.transactionType?.toLowerCase() == 'internal-transfer') {
        if (this.isCreateSloaToggleFlagEnabled && this.hasMmInitiateAccess) {
          this.store.dispatch(StandingAuthInternalTransferActions.clearStandingAuthInternalTransferResponse());
          this.store.dispatch(TransactionFormActions.callInternalTransferVerifyCustomRule({ transactionForm: this.transactionForm }));
          this.store.select(selectStandingAuthInternalTransferCustomRuleSplitCallHasError).pipe(takeUntil(this.destroy$)).subscribe(hasError => {
            if (hasError) {
              this.toggleOff = hasError;
            }
          });
        }
      }
    }
    else {
      if (this.isCreateSloaToggleFlagEnabled && this.hasMmInitiateAccess) {
        this.store.select(selectStandingAuthWireInfo).pipe(take(1)).subscribe(state => {
          this.store.dispatch(StandingAuthWireActions.clearStandingAuthWireAuthResponse({ state }));
        });
        this.store.dispatch(StandingAuthInternalTransferActions.clearStandingAuthInternalTransferResponse());
      }
    }
  }

  onToggleOff(): void {
    if (this.toggleOff === false) {
      this.toggleOff = true;
      this.isSubmitAndCreate.emit(false);
    }
  }

  get showSubmitAndCreateSection(): boolean {
    return (this.showSubmitAndCreate ?? false) && !this.isPalAccount;
  }

  get title(): string {
    return this.isBundledOnetime ? this.createBundledStandingAuthTitle : this.createStandingAuthTitle;
  }

  get detail(): string {
    return this.isBundledOnetime ? this.createStandingAuthBundledDetail : this.createStandingAuthDetail;
  }
}
